import { Button } from "@faire/web--source/slate/Button";
import { Column } from "@faire/web--source/slate/Layout";
import { Spacer } from "@faire/web--source/slate/spacing";
import { Typography } from "@faire/web--source/slate/Typography";
import { StrictLocalMsg } from "@faire/web--source/ui/lib/localization";
import brandPortalProducts from "@faire/web-api--source/routes/www/brand-portal/my-shop/products";
import React, { JSX } from "react";

import { Bold } from "../common/Bold";
import { LiItem } from "../common/LiItem";

export const AddSingleProductIA = () => {
  return (
    <Column>
      <Typography>
        <StrictLocalMsg
          defaultMessage="You can add a single product from the Products page on your Faire shop."
          description={{
            text: "Introduction to adding a single product",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <Typography>
        <StrictLocalMsg
          defaultMessage="Here’s how you can do that:"
          description={{
            text: "Header for steps to add a single product",
          }}
        />
      </Typography>
      <Spacer height="1x" />
      <ol>
        <LiItem step={1}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Navigate to your <LinkFormatting>Products</LinkFormatting> page."
              values={{
                LinkFormatting: (chunks: JSX.Element) =>
                  ProductsLinkFormatting(chunks),
              }}
              description={{
                text: "First step to add a single product",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={2}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Select the <BoldFormatting>Add products</BoldFormatting> dropdown."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Second step to add a single product",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={3}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Choose the <BoldFormatting>Add a single product</BoldFormatting> option."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Third step to add a single product",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={4}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Complete the information fields as prompted. You’ll be required to add a <BoldFormatting>product name</BoldFormatting> and <BoldFormatting>product type</BoldFormatting> before other fields will populate. Fields noted with an asterisk are required to publish your new product."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fourth step to add a single product",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={5}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="You may also create <BoldFormatting>product options</BoldFormatting>, set the product as a <BoldFormatting>preorder</BoldFormatting> or <BoldFormatting>pre-pack</BoldFormatting>, and update details like inventory for each variation."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Fifth step to add a single product",
              }}
            />
          </Typography>
        </LiItem>
        <Spacer height="1x" />
        <LiItem step={6}>
          <Typography>
            <StrictLocalMsg
              defaultMessage="Once you’ve finished, select <BoldFormatting>Publish product</BoldFormatting> at the bottom of the page. If any required fields are missing, you will be prompted to add missing info, or save the product as a draft."
              values={{
                BoldFormatting,
              }}
              description={{
                text: "Sixth step to add a single product",
              }}
            />
          </Typography>
        </LiItem>
      </ol>
    </Column>
  );
};

const ProductsLinkFormatting = (chunks: JSX.Element) => (
  <Button variant="tertiary" linkTo={brandPortalProducts()} target="_blank">
    <Bold>{chunks}</Bold>
  </Button>
);

const BoldFormatting = (chunks: JSX.Element) => <Bold>{chunks}</Bold>;
